import type { Geotab } from "@fairfleet/geotab";

import { compressData } from "@/utilities/compression";

export const ADDIN_DATA_MAX_CHARS = 10000;

export class AddInDataTooLargeError extends Error {
  readonly length: number;

  constructor(length: number) {
    super(`AddInData details too large: ${length} > ${ADDIN_DATA_MAX_CHARS} characters`);
    this.name = "AddInDataTooLargeError";
    this.length = length;
  }
}

export function buildDetails(
  plain: Record<string, string | number>,
  payload: unknown,
): Record<string, string | number> {
  const details = { ...plain, ...compressData(payload as object) };
  const length = JSON.stringify(details).length;
  if (length > ADDIN_DATA_MAX_CHARS) throw new AddInDataTooLargeError(length);
  return details;
}

export interface UpsertArgs {
  geotabClient: Geotab;
  addInId: string;
  lookup: { field: string; value: string };
  plain: Record<string, string | number>;
  payload: unknown;
}

interface RowLike {
  id: string;
  details: { updatedAt?: number };
}

const updatedAtOf = (row: RowLike) => row.details?.updatedAt ?? 0;

function newestOf<R extends RowLike>(rows: R[]): R {
  return rows.reduce((best, row) => (updatedAtOf(row) > updatedAtOf(best) ? row : best));
}

export async function upsertAddInData({
  geotabClient,
  addInId,
  lookup,
  plain,
  payload,
}: UpsertArgs): Promise<string> {
  const details = buildDetails(plain, payload);
  if (lookup.value.includes('"')) throw new Error("invalid lookup value");

  const existing = (await geotabClient.get(
    "AddInData",
    { addInId, whereClause: `${lookup.field} = "${lookup.value}"` },
    50,
  )) as unknown as RowLike[];

  if (existing.length > 0) {
    const { id } = newestOf(existing);
    await geotabClient.call("Set", { typeName: "AddInData", entity: { id, addInId, details } });
    return id;
  }
  return geotabClient.call("Add", { typeName: "AddInData", entity: { addInId, details } });
}

export function pickNewestPerKey<R extends RowLike>(
  rows: R[],
  keyOf: (r: R) => string,
): { keep: R[]; duplicates: R[] } {
  const newest = new Map<string, R>();
  for (const row of rows) {
    const key = keyOf(row);
    const current = newest.get(key);
    if (!current || updatedAtOf(row) > updatedAtOf(current)) newest.set(key, row);
  }
  const keep = [...newest.values()];
  const kept = new Set(keep);
  return { keep, duplicates: rows.filter(r => !kept.has(r)) };
}
